import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  BINARY_DECOMPOSITION,
  INVENTORY_UI,
  SNIPPET_DETECTION,
  VENDORED_DEPENDENCY_DETECTION,
} from '../../../../../shared/featureFlags/flagNames';
import Locator from '../../../../../shared/Locator';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import releaseGroupSelectors from '../../../../redux/project_grouping/selectors';
import { checkProjectScope } from '../types';
import type { ProjectGroupRelease } from '../../../../types';
import type { InventoryScope, UseInventoryDropdownFeaturesResult } from '../types';

const isBinaryProject = (projectId: string): boolean => {
  return Locator.parse(projectId).fetcher === 'binary';
};

const shouldShowBinaryForReleaseGroup = (release: ProjectGroupRelease | null, isBinaryEnabled: boolean): boolean => {
  return isBinaryEnabled && (release?.projects?.some(project => isBinaryProject(project.projectId)) ?? false);
};

const shouldShowBinaryForProject = (projectId: string, isBinaryEnabled: boolean): boolean => {
  return isBinaryEnabled && isBinaryProject(projectId);
};

/**
 * A hook for figuring out which features of the inventory dropdown should be shown.
 *
 * @param {InventoryScope} scope - The scope of the inventory dropdown.
 *
 */
export function useInventoryDropdownFeatures(scope: InventoryScope): UseInventoryDropdownFeaturesResult {
  const isProjectScope = checkProjectScope(scope);

  //  Note: that this part relies on the Release Groups Redux state
  const release = useSelector(releaseGroupSelectors.getRelease);

  //  Feature flag checks
  const isInventoryUIEnabled = useIsFeatureEnabled(INVENTORY_UI);
  const isSnippetScanningEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);

  // Only consider True for snippets if we're looking at a project, since RG snippets are not supported yet.
  const shouldShowSnippets = isSnippetScanningEnabled && isProjectScope;
  // Vendored dependencies are available for both project and release group scopes
  const shouldShowVendored = isVendoredDependenciesEnabled;
  //  We only show binary analysis for Projects that are for the binary fetcher or for Release Groups (since we don't have a binary project or not)
  //  Memoize because we don't want to iterate release.projects on every render.
  const shouldShowBinaryAnalysis = useMemo(() => isProjectScope
    ? shouldShowBinaryForProject(scope.revision, isBinaryDecompositionEnabled)
    : shouldShowBinaryForReleaseGroup(release, isBinaryDecompositionEnabled),
  [isProjectScope, scope, release, isBinaryDecompositionEnabled]);

  const shouldShowAnyGatedFeatures = shouldShowSnippets || shouldShowBinaryAnalysis || shouldShowVendored;
  //  They need to have at least one of the gated features enabled to show the inventory dropdown
  const shouldShowInventory = isInventoryUIEnabled && shouldShowAnyGatedFeatures;

  return useMemo(() => ({
    shouldShowInventory,
    shouldShowSnippets,
    shouldShowBinaryAnalysis,
    shouldShowVendored,
  }), [shouldShowInventory, shouldShowSnippets, shouldShowBinaryAnalysis, shouldShowVendored]);
}
